import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Paper,
    Typography,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
} from "@mui/material";
import { handleDateTimeDefault } from "../../../helpers/utils";

const Field = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
            {value || "—"}
        </Typography>
    </Grid>
);

const ShopCustomerDetailsDialog = ({ open, onClose, customer, loading, error }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
            {customer?.name || "Shop customer"}{" "}
            {customer && (
                <Chip
                    size="small"
                    sx={{ ml: 1 }}
                    label={customer.status === "blocked" ? "Blocked" : "Active"}
                    color={customer.status === "blocked" ? "error" : "success"}
                />
            )}
        </DialogTitle>
        <DialogContent dividers>
            {loading && <CircularProgress />}
            {error && <Alert severity="error">{error}</Alert>}
            {customer && !loading && (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Account
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Field label="Mobile" value={customer.mobile} />
                                <Field label="Email" value={customer.email} />
                                <Field label="Login methods" value={customer.has_password ? "WhatsApp OTP, password" : "WhatsApp OTP"} />
                                <Field label="Last login" value={customer.last_login && handleDateTimeDefault(customer.last_login)} />
                                <Field label="Joined" value={handleDateTimeDefault(customer.created_at)} />
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Referral and wallet
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Field label="Referral code" value={customer.referral_code} />
                                <Field
                                    label="Referred by"
                                    value={
                                        customer.referrer
                                            ? `${customer.referrer.name} (${customer.referrer.referral_code})`
                                            : "Direct sign-up"
                                    }
                                />
                                <Field label="Wallet balance" value={`₹${customer.wallet?.balance || "0.00"}`} />
                                <Field label="Pending rewards" value={`₹${customer.wallet?.pending_amount || "0.00"}`} />
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2">Customers referred ({customer.referrals?.length || 0})</Typography>
                            <Divider sx={{ my: 1 }} />
                            {customer.referrals?.length ? (
                                <List dense disablePadding>
                                    {customer.referrals.map((r) => (
                                        <ListItem key={r.id} disableGutters>
                                            <ListItemText primary={r.name} secondary={r.mobile} />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No referrals yet
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2">Saved addresses ({customer.addresses?.length || 0})</Typography>
                            <Divider sx={{ my: 1 }} />
                            {customer.addresses?.length ? (
                                customer.addresses.map((a) => (
                                    <Typography key={a.id} variant="body2" sx={{ mb: 1 }}>
                                        {a.name}, {a.address_line1}, {a.city}, {a.state} - {a.pincode}
                                        {a.is_default ? " (default)" : ""}
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No saved addresses
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2">Recent shop orders ({customer.orders?.length || 0})</Typography>
                            <Divider sx={{ my: 1 }} />
                            {customer.orders?.length ? (
                                customer.orders.map((o) => (
                                    <Typography key={o.id} variant="body2">
                                        {o.order_number} · ₹{o.total_amount} · {o.status}
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No orders yet
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Close</Button>
        </DialogActions>
    </Dialog>
);

export default ShopCustomerDetailsDialog;
